import React, { useEffect, useState } from 'react';
import { CheckIcon, ChevronDownIcon, MapPinIcon } from 'lucide-react';
import { MarketDefinition, marketRegions, placesInRegion } from '../data/marketRegions';
const markets = marketRegions;

// Some brands only distribute into a defined set of markets.
const brandMarketDefaults: Record<string, {
  regions: string[];
  keep: Record<string, string[]>;
}> = {
  Nisolo: {
    regions: ['north-america', 'europe', 'asia', 'oceania'],
    keep: {
      asia: ['Japan', 'South Korea', 'Singapore'],
      oceania: ['Australia', 'New Zealand']
    }
  }
};
const placesIn = placesInRegion;
export function GeographySelector({
  brand
}: {
  brand?: string;
}) {
  const preset = brand ? brandMarketDefaults[brand] : undefined;
  const [marketsEnabled, setMarketsEnabled] = useState<string[]>(preset?.regions ?? markets.map(market => market.id));
  const [openMarket, setOpenMarket] = useState<string | null>(null);
  // Places switched off inside an otherwise enabled region.
  const [excluded, setExcluded] = useState<Record<string, string[]>>(() => {
    if (!preset) return {};
    return Object.fromEntries(Object.entries(preset.keep).map(([marketId, keep]) => [marketId, placesIn(marketId).filter(place => !keep.includes(place))]));
  });

  // Switching brands reloads that brand's distribution footprint.
  useEffect(() => {
    setMarketsEnabled(preset?.regions ?? markets.map(market => market.id));
    setExcluded(preset ? Object.fromEntries(Object.entries(preset.keep).map(([marketId, keep]) => [marketId, placesIn(marketId).filter(place => !keep.includes(place))])) : {});
  }, [brand]);
  const toggleMarket = (market: string) => {
    setMarketsEnabled(current => current.includes(market) ? current.filter(item => item !== market) : [...current, market]);
  };
  const togglePlace = (market: string, place: string) => {
    setExcluded(current => {
      const list = current[market] ?? [];
      return {
        ...current,
        [market]: list.includes(place) ? list.filter(item => item !== place) : [...list, place]
      };
    });
  };
  const setAllPlaces = (market: MarketDefinition, enabled: boolean) => {
    setExcluded(current => ({
      ...current,
      [market.id]: enabled ? [] : market.groups.flatMap(group => group.places)
    }));
  };
  return <section className="mt-5 rounded-3xl border border-ink/10 bg-paper p-5 shadow-sm sm:p-7" aria-labelledby="geography-heading" data-id="element-1597" data-mp="3tza22mlh02do">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between" data-id="element-1598" data-mp="2rp80fogsjmk">
        <div data-id="element-1599" data-mp="7j78l2mksi70">
          <div className="flex items-center gap-2" data-id="element-1600" data-mp="7kqpmvg22x9u">
            <MapPinIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-1601" data-mp="bl13sve04tzs" />
            <h2 id="geography-heading" className="text-lg font-semibold" data-id="element-1602" data-mp="bn5b7nb40b4t">Market access</h2>
          </div>
          <p className="mt-1 text-sm leading-relaxed text-ink/60" data-id="element-1603" data-mp="giat8b1e55nm">Choose the regions where we can market and sell your approved inventory.</p>
        </div>
        <span className="text-sm font-medium text-ink/55" data-id="element-1604" data-mp="vnyfaz1d7dvz">{marketsEnabled.length} of {markets.length} regions active</span>
      </div>

      <div className="mt-6 overflow-hidden rounded-2xl border border-ink/8 bg-[#ece8df] px-3 py-4 sm:px-6 sm:py-6" data-id="element-1605" data-mp="qq5bgcnjavyv">
        <div className="relative mx-auto aspect-[2.15/1] w-full" role="group" aria-label="Interactive world market map" data-id="element-1606" data-mp="hndxa539xm3j">
          <img src="https://cdn.magicpatterns.com/uploads/767dGYamSwJgJFZTEhNNfX/world.svg" alt="World map showing regions where inventory can be sold" className="h-full w-full object-contain opacity-90" data-id="element-1607" data-mp="1ee6ghbli7u9j" />
          {markets.map(market => {
          const active = marketsEnabled.includes(market.id);
          const excludedCount = (excluded[market.id] ?? []).length;
          return <button key={market.id} type="button" onClick={() => toggleMarket(market.id)} aria-pressed={active} aria-label={`Toggle ${market.label}`} className={`absolute ${market.position} -translate-x-1/2 -translate-y-1/2 rounded-full border px-2.5 py-1 text-xs font-semibold shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[#BB5E40]/30 sm:px-3 sm:py-1.5 ${active ? 'border-[#BB5E40] bg-[#BB5E40] text-cream hover:bg-[#94452E]' : 'border-dashed border-ink/25 bg-[#d9d5cc] text-ink/45 hover:text-ink/70'}`} data-id="element-1608" data-mp="1x5fp7ingaf02">
                <span className="flex items-center gap-1.5" data-id="element-1609" data-mp="20mtnm7rif5rt">
                  <span className={`h-1.5 w-1.5 rounded-full ${active ? 'bg-cream' : 'bg-ink/25'}`} data-id="element-1610" data-mp="1mt2pdvj8b8be" />
                  {market.label}
                  {active && excludedCount > 0 && <span className="rounded-full bg-cream/25 px-1.5 text-[10px] font-semibold" data-id="element-1611" data-mp="2ejyq5xs0lmib">−{excludedCount}</span>}
                </span>
              </button>;
        })}
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-2" aria-label="Available regions" data-id="element-1612" data-mp="7uexuk9iyuyt">
        {markets.map(market => {
        const active = marketsEnabled.includes(market.id);
        return <button key={market.id} type="button" onClick={() => toggleMarket(market.id)} aria-pressed={active} className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-sm font-semibold transition-colors ${active ? 'border-[#BB5E40] bg-[#BB5E40] text-cream' : 'border-ink/10 bg-cream text-ink/65 hover:border-[#BB5E40]/35'}`} data-id="element-1613" data-mp="hodux4uqrojw">
              {active && <CheckIcon className="h-3.5 w-3.5" strokeWidth={3} aria-hidden="true" data-id="element-1614" data-mp="3q1bb5opr5z9c" />}
              {market.label}
            </button>;
      })}
      </div>

      <div className="mt-6 border-t border-ink/10" data-id="element-1615" data-mp="15cf2aag6oyxq">
        {markets.map(market => {
        const enabled = marketsEnabled.includes(market.id);
        const excludedList = excluded[market.id] ?? [];
        const total = market.groups.reduce((sum, group) => sum + group.places.length, 0);
        const open = openMarket === market.id;
        return <div key={market.id} className="border-b border-ink/10 py-5" data-id="element-1616" data-mp="1mbf5f5v7ueyy">
              <button type="button" onClick={() => setOpenMarket(open ? null : market.id)} aria-expanded={open} aria-controls={`${market.id}-rules`} disabled={!enabled} className="flex w-full items-center justify-between gap-4 text-left disabled:cursor-not-allowed disabled:opacity-45" data-id="element-1617" data-mp="1rbkzesoykdk7">
                <div data-id="element-1618" data-mp="18et7juvcoq4j">
                  <p className="text-sm font-semibold text-ink" data-id="element-1619" data-mp="18fbfvpvaeust">{market.label}</p>
                  <p className="mt-1 text-xs leading-relaxed text-ink/55" data-id="element-1620" data-mp="aw07nzv6nj2s">
                    {enabled ? `${total - excludedList.length} of ${total} markets enabled` : `Enable ${market.label} above to manage individual markets`}
                  </p>
                </div>
                <ChevronDownIcon className={`h-4 w-4 shrink-0 text-ink/45 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-1621" data-mp="2p410k6gzcsv7" />
              </button>

              {open && enabled && <div id={`${market.id}-rules`} className="mt-5 rounded-2xl border border-ink/10 bg-cream/55 p-4 sm:p-5" data-id="element-1622" data-mp="2a8l5pz8kssxr">
                  <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-1623" data-mp="25hcyn9upxjnr">
                    <p className="text-xs leading-relaxed text-ink/60" data-id="element-1624" data-mp="21g1cjeb1zgkb">Deselect anywhere you do not want inventory sold. Greyed entries are excluded.</p>
                    <div className="flex gap-2" data-id="element-1625" data-mp="1wj17n6pxwjus">
                      <button type="button" onClick={() => setAllPlaces(market, true)} className="rounded-full border border-ink/15 px-2.5 py-1 text-[11px] font-semibold text-ink/65 transition-colors hover:bg-paper" data-id="element-1626" data-mp="1rimytmgil5vp">
                        Select all
                      </button>
                      <button type="button" onClick={() => setAllPlaces(market, false)} className="rounded-full border border-ink/15 px-2.5 py-1 text-[11px] font-semibold text-ink/65 transition-colors hover:bg-paper" data-id="element-1627" data-mp="31f0j6ufz3t3r">
                        Clear all
                      </button>
                    </div>
                  </div>

                  <div className="mt-5 space-y-5" data-id="element-1628" data-mp="2ip2968sm7pxh">
                    {market.groups.map((group, index) => <div key={group.label ?? index} data-id="element-1629" data-mp="2noopir3qpmai" data-mpi={index}>
                        {group.label && <p className="text-[11px] font-semibold uppercase tracking-wider text-ink/45" data-id="element-1630" data-mp="28re5gtd17836">{group.label}</p>}
                        <div className={`flex flex-wrap gap-2 ${group.label ? 'mt-2.5' : ''}`} data-id="element-1631" data-mp="2dra92rhtgxi7">
                          {group.places.map(place => {
                    const off = excludedList.includes(place);
                    return <button key={place} type="button" onClick={() => togglePlace(market.id, place)} aria-pressed={!off} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${off ? 'border-ink/10 bg-paper text-ink/40 hover:border-[#BB5E40]/35 hover:text-ink/65' : 'border-[#BB5E40]/35 bg-[#BB5E40]/5 text-[#94452E] hover:bg-[#BB5E40]/10'}`} data-id="element-1632" data-mp="242msnakzqjna">
                                {!off && <CheckIcon className="h-3 w-3" strokeWidth={3} aria-hidden="true" data-id="element-1633" data-mp="2poxd3sash6fg" />}
                                {place}
                              </button>;
                  })}
                        </div>
                      </div>)}
                  </div>
                </div>}
            </div>;
      })}
      </div>
    </section>;
}